import {FontAwesomeIcon as Icon} from "@fortawesome/react-fontawesome"
import {faTachometerAlt, faCalendar, faFire, faBolt, faGasPump} from "@fortawesome/free-solid-svg-icons"
import React, {Ref} from "react"
import {connect} from "react-redux"
import {RouteComponentProps} from 'react-router-dom'
import BottomScrollListener from 'react-bottom-scroll-listener'
import {GlobalState} from "../../state"
import AppNavigation from "../AppNavigation"
import AuctionCard from "../Auctions/AuctionCard"
import {Auction, Subjects, Vehicle} from "domain-models"
import LoadingIndicator from '../../components/LoadingIndicator'
import MissingData from '../../components/MissingData'
import {Panel} from '../../components/Panel'
import {hasFlag, make, model, monthYear, number, vehicleAttribute} from 'filters'
import Trans from '../../components/Trans'
import SwitchAuctionView from '../Auctions/SwitchAuctionView'
import ImagePlaceholder from '../../components/ImagePlaceholder'
import _ from 'lodash'

type ClaimedRound = {
  Id: number
  AuctionId: number
  SpeedAuktionClaimedOn: string
  SpeedAuktionClaimedBy: number
  auction: Auction
  highest_bid: {
    Bid: number
  } | null
}

type ClaimedVehiclesPageProps = RouteComponentProps & {
  auction_view: 'grid' | 'list'
}

type ClaimedVehiclesPageState = {
  busy: boolean
  rounds: ClaimedRound[]
  error: string | null
  currentPage: number
  lastPage: number
  total: number
  nextPageUrl: string | null
}

class ClaimedVehiclesPage extends React.Component<ClaimedVehiclesPageProps, ClaimedVehiclesPageState> {
  constructor(props: ClaimedVehiclesPageProps) {
    super(props)
    this.state = {
      busy: false,
      rounds: [],
      error: null,
      currentPage: 1,
      lastPage: 1,
      total: 0,
      nextPageUrl: null
    }
  }

  componentDidMount() {
    this.loadClaimed()
  }

  getDateFiltersFromUrl(): {from?: string, to?: string} {
    const params = new URLSearchParams(this.props.location.search)
    return {
      from: params.get('from') || undefined,
      to: params.get('to') || undefined
    }
  }

  loadClaimed = async (page: number = 1, append: boolean = false) => {
    this.setState({busy: true, error: null})
    try {
      const {from, to} = this.getDateFiltersFromUrl()
      const params = new URLSearchParams()
      params.set('page', page.toString())
      if (from) params.set('from', from)
      if (to) params.set('to', to)
      
      const response = await window.axios.get<{
        data: ClaimedRound[],
        current_page: number,
        last_page: number,
        total: number,
        next_page_url: string | null
      }>(`/api/speed-auktion/claimed?${params.toString()}`)
      
      this.setState({
        rounds: append ? [...this.state.rounds, ...(response.data?.data || [])] : (response.data?.data || []),
        currentPage: response.data?.current_page || 1,
        lastPage: response.data?.last_page || 1,
        total: response.data?.total || 0,
        nextPageUrl: response.data?.next_page_url || null,
        busy: false
      })
    } catch (err) {
      this.setState({
        busy: false,
        error: 'Fehler beim Laden der gekauften Fahrzeuge',
      })
    }
  }

  appendData = async () => {
    if (this.state.nextPageUrl && !this.state.busy) {
      await this.loadClaimed(this.state.currentPage + 1, true)
    }
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  renderBadge = (round: ClaimedRound) => {
    return (
      <div className="px-2 py-1">
        <span className="px-2 py-1 bg-green-100 border border-green-400 rounded inline-block">
          <Icon icon="check-circle" className="text-green-500"/>
          <span className="ml-1 text-green-700 font-semibold text-sm"><Trans>Gekauft</Trans></span>
        </span>
      </div>
    )
  }

  renderPrice = (round: ClaimedRound) => {
    const price = round.highest_bid?.Bid
    if (!price) return null
    
    return (
      <div className="px-2 py-2">
        <div className="p-2 bg-green-50 rounded">
          <span className="text-sm text-gray-600"><Trans>Kaufpreis</Trans>:</span>
          <span className="text-xl font-bold text-green-600 ml-2">
            {number(price, 0, '€')}
          </span>
        </div>
      </div>
    )
  }

  renderClaimedInfo = (round: ClaimedRound) => {
    return (
      <div className="p-2 mt-auto">
        <div className="bg-gray-100 rounded p-2 text-sm text-gray-600">
          <Icon icon="calendar-check" className="mr-1"/>
          <Trans>Gekauft am</Trans>: {this.formatDate(round.SpeedAuktionClaimedOn)}
        </div>
      </div>
    )
  }

  getSubject(round: ClaimedRound): {subject?: Subjects, vehicle?: Vehicle} {
    const vehicle = _.first(round.auction.subjects)?.vehicle
    const subject = vehicle
    return {subject, vehicle}
  }

  renderListEntry = (round: ClaimedRound) => {
    const {subject, vehicle} = this.getSubject(round)
    if (!subject) return null

    const firstImage = _.first(subject?.images || [])
    const dealer = subject.dealer
    const price = round.highest_bid?.Bid

    return (
      <tr key={round.Id} className="w-full hover:bg-gray-200">
        {firstImage ? (
          <td className="bg-cover bg-center h-16 border-t border-b px-2 py-1 min-w-16" style={{backgroundImage: "url(" + firstImage + ")"}}/>
        ) : (
          <td className="h-16 w-16 border-t border-b px-2 py-1 min-w-16"><ImagePlaceholder small={true}/></td>
        )}

        <td className="truncate px-2 py-1 border">
          <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm">
            <Icon icon="check-circle" className="mr-1"/>
            <Trans>Gekauft</Trans>
          </span>
        </td>

        <td className="truncate px-2 py-1 border">{make(subject.Make)}</td>
        <td className="truncate px-2 py-1 border">{model(subject.Make, subject.Model)}</td>
        <td className="truncate px-2 py-1 border w-32 md:w-64">
          <p>
            <span className="px-1 hidden md:block">{dealer?.FirmExtra}</span>
            <span className="px-1 hidden md:block">{dealer?.Street}</span>
            <span className="px-1 md:block">{dealer?.PostalCode}</span>
            <span className="px-1 md:block">{dealer?.City}</span>
          </p>
        </td>
        <td className="px-2 py-1 border">
          {price ? (
            <span className="text-green-600 font-bold">{number(price, 0, '€')}</span>
          ) : '-'}
        </td>

        {vehicle ? (
          <>
            <td className="p-2 font-semibold">{number(vehicle.Kilometers, 0, 'km')}</td>
            <td className="p-2 font-semibold">{monthYear(vehicle.RegistrationDate)}</td>
            <td className="p-2 font-semibold">{number(vehicle.Power, 0, 'kw')}</td>
            <td className="p-2 font-semibold"><Trans>{vehicleAttribute('Transmission', vehicle.Transmission)}</Trans></td>
            <td className="p-2 font-semibold"><Trans>{vehicleAttribute('Fuel', vehicle.Fuel)}</Trans></td>
            <td className="p-2 font-semibold">
              {hasFlag(vehicle, 'SellFlags', 32768) ? (
                <Trans>MwST. ausweisbar</Trans>
              ) : (
                <Trans>MwST. nicht ausweisbar</Trans>
              )}
            </td>
          </>
        ) : null}

        <td className="border-t border-b px-2 py-1 text-sm text-gray-600">
          {this.formatDate(round.SpeedAuktionClaimedOn)}
        </td>
      </tr>
    )
  }

  render() {
    const {busy, rounds, error} = this.state
    const {auction_view} = this.props

    return (
      <BottomScrollListener onBottom={this.appendData}>
        {(scrollRef: Ref<HTMLDivElement>) => (
          <div className='block md:flex overflow-x-auto overflow-y-scroll scrolling-smooth scrolling-touch h-full' ref={scrollRef}>
            <div className='hidden lg:block'>
              <AppNavigation />
            </div>
            <div className='w-full flex flex-col'>
              {error && (
                <div className="m-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                  {error}
                  <button onClick={() => this.setState({error: null})} className="float-right">
                    <Icon icon="times"/>
                  </button>
                </div>
              )}

              {
                busy && !rounds.length ?
                  <LoadingIndicator />
                  :
                  auction_view === 'grid' && rounds && rounds.length ?
                    <div className={'flex flex-col'}>
                      <div className={'pt-6 pl-8 font-semibold'}>
                        <SwitchAuctionView />
                      </div>

                      <div className='flex flex-col md:flex-row flex-wrap w-full mt-0'>
                        {
                          rounds.map(round => (
                            <AuctionCard
                              key={round.Id}
                              auction={round.auction}
                              badgeContent={this.renderBadge(round)}
                              renderPrice={() => this.renderPrice(round)}
                              renderActions={() => this.renderClaimedInfo(round)}
                            />
                          ))
                        }
                      </div>

                      {
                        busy ?
                          <LoadingIndicator />
                          : null
                      }
                    </div>
                    : rounds && rounds.length ?
                      <Panel classNames={'pr-10 md:pl-2'} overflow={' none '}>
                        <table className={'table-auto my-2 mx-4 text-gray-800 w-full'}>
                          <thead>
                            <tr>
                              <td className={'p-2 font-semibold'}>
                                <SwitchAuctionView />
                              </td>
                              <td className={'p-2 font-semibold'}><Trans>Status</Trans></td>
                              <td className={'p-2 font-semibold'}><Trans>Marke</Trans></td>
                              <td className={'p-2 font-semibold'}><Trans>Modell</Trans></td>
                              <td className={'p-2 font-semibold'}><Trans>Standort</Trans></td>
                              <td className={'p-2 font-semibold'}><Trans>Preis</Trans></td>
                              <td className={'p-2 font-semibold'}>
                                <Icon className='text-gray-600 font-normal fa-fw' icon={faTachometerAlt} /></td>
                              <td className={'p-2 font-semibold'}>
                                <Icon className='text-gray-600 font-normal fa-fw' icon={faCalendar} /></td>
                              <td className={'p-2 font-semibold'}>
                                <Icon className='text-gray-600 font-normal fa-fw' icon={faFire} /></td>
                              <td className={'p-2 font-semibold'}>
                                <Icon className='text-gray-600 font-normal fa-fw' icon={faBolt} /></td>
                              <td className={'p-2 font-semibold'}>
                                <Icon className='text-gray-600 font-normal fa-fw' icon={faGasPump} /></td>
                              <td className={'p-2 font-semibold'}>
                                <Icon className='text-gray-600 font-normal fa-fw' icon='search-dollar' /></td>
                              <td className={'p-2 font-semibold'}><Trans>Gekauft am</Trans></td>
                            </tr>
                          </thead>
                          <tbody>
                            {rounds.map(round => this.renderListEntry(round))}
                          </tbody>
                        </table>

                        {busy ? <LoadingIndicator /> : null}
                      </Panel>
                      : null
              }

              {
                !busy && (!rounds || !rounds.length) ?
                  <MissingData>
                    <Trans>Keine gekauften Fahrzeuge gefunden</Trans>
                  </MissingData>
                  : null
              }
            </div>
          </div>
        )}
      </BottomScrollListener>
    )
  }
}

export default connect((state: GlobalState) => ({
  auction_view: state.user.app_settings && state.user.app_settings.auction_view ? state.user.app_settings.auction_view : 'grid'
}))(ClaimedVehiclesPage)
